// Copyright (c) 2026 Interpaste, all rights reserved
// @author: norelock <https://gitlab.com/mvvnlight>

import { ROOT } from '../env'
import { Logger, PlayerSlot } from '../utils'
import { scFrameClass } from '../styles'
import {
  SOUNDCLOUD_TRACK_API,
  SOUNDCLOUD_WIDGET_API_SCRIPT_REGEX,
  SOUNDCLOUD_WIDGET_API_SCRIPT_SELECTOR,
  SOUNDCLOUD_WIDGET_API_URL,
  SOUNDCLOUD_WIDGET_URL,
} from '../constants'
import type { HostPayload, PlayerBridge, SCWidget, SCProgressEvent } from '../types'

export class SoundCloudPlayer implements PlayerBridge {
  private widget: SCWidget | null = null
  private data: HostPayload | null = null
  private slot = new PlayerSlot('soundcloud', ROOT)
  private logger: Logger = Logger.create('SCPlayer')

  private apiLoaded = false
  private isPlaying = false
  private finished = false
  private trackDuration = 0
  private stoppedByHost = false
  private _externalPause = false
  private _postMessage: (msg: string) => void
  private readonly onVisibilityChange: () => void

  constructor(postMessage: (msg: string) => void) {
    this._postMessage = postMessage
    this.onVisibilityChange = () => {
      if (document.hidden) return
      this.tryResumeAfterVisibility('visibilitychange')
    }
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  load(data: HostPayload): void {
    this.data = data
    this.finished = false
    this.stoppedByHost = false
    this._externalPause = false

    if (this.widget) {
      this.swap()
    } else if (this.apiLoaded) {
      this.initWidget()
    } else {
      this.loadAPI()
    }
  }

  handleCommand(msg: string): boolean {
    const widget = this.widget

    if (msg === 'durationCheck') {
      widget?.getDuration(d => this._postMessage(`durationCheck=${Math.round(d / 1000)}`))
      return true
    }
    if (msg === 'unMute') {
      widget?.setVolume(this.data?.volume ?? 100)
      return true
    }
    if (msg.startsWith('stopVideo')) {
      this.stoppedByHost = true
      widget?.pause()
      return true
    }
    if (msg.startsWith('setVolume=')) {
      const vol = parseInt(msg.split('=').pop()!, 10)
      if (!isNaN(vol)) widget?.setVolume(vol)
      return true
    }
    if (msg.startsWith('seekTo=')) {
      const seconds = parseInt(msg.split('=').pop()!, 10)
      if (!isNaN(seconds)) {
        widget?.seekTo(seconds * 1000)
        this.logger.debug('seekTo', seconds)
      }
      return true
    }
    if (msg === 'pauseVideo') {
      this._externalPause = true
      widget?.pause()
      this.logger.debug('pauseVideo')
      return true
    }
    if (msg === 'playVideo') {
      this._externalPause = false
      widget?.play()
      this.logger.debug('playVideo')
      return true
    }

    return false
  }

  destroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange)

    // Unbind all widget events before removing
    if (this.widget) {
      const { Events } = window.SC.Widget
      this.widget.unbind(Events.READY)
      this.widget.unbind(Events.PLAY)
      this.widget.unbind(Events.PAUSE)
      this.widget.unbind(Events.FINISH)
      this.widget.unbind(Events.PLAY_PROGRESS)
      this.widget.unbind(Events.ERROR)
      this.widget = null
    }
    // Clear iframe from DOM so the SC widget is fully GC'd
    this.slot.clear()
    this.slot.destroy()
    this.logger.debug('destroyed')
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  private loadAPI(): void {
    const scriptAlreadyPresent = !!document.querySelector(SOUNDCLOUD_WIDGET_API_SCRIPT_SELECTOR)
      || Array.from(document.scripts).some((script) => SOUNDCLOUD_WIDGET_API_SCRIPT_REGEX.test(script.src))

    if (scriptAlreadyPresent) {
      // API script already in DOM (e.g. re-init after destroy), go straight to init
      this.apiLoaded = true
      this.initWidget()
      return
    }

    const script = document.createElement('script')
    script.src = SOUNDCLOUD_WIDGET_API_URL
    script.onload = () => {
      this.logger.info('SoundCloud Widget API loaded')
      this.apiLoaded = true
      this.initWidget()
    }
    document.head.appendChild(script)
  }

  private initWidget(): void {
    if (!this.data || !this.apiLoaded) return

    this.slot.show()
    const iframe = this.slot.node({
      tag: 'iframe',
      key: 'sc-widget',
      id: 'sc-widget',
      className: scFrameClass,
      attrs: {
        allow: 'autoplay',
        src: this.buildWidgetUrl(this.data.id),
      },
    })

    this.widget = window.SC.Widget(iframe)
    this.bindEvents()
  }

  private buildWidgetUrl(trackId: string): string {
    const params = new URLSearchParams({
      url: `${SOUNDCLOUD_TRACK_API}/${trackId}`,
      auto_play: 'true',
      hide_related: 'true',
      show_comments: 'false',
      show_user: 'false',
      show_reposts: 'false',
      show_teaser: 'false',
      visual: 'true',
      single_active: 'true',
    })
    return `${SOUNDCLOUD_WIDGET_URL}/?${params.toString()}`
  }

  private bindEvents(): void {
    const { Events } = window.SC.Widget
    const w = this.widget!

    w.bind(Events.READY,         () => this.onReady())
    w.bind(Events.PLAY,          () => { this.isPlaying = true; this.finished = false; })
    w.bind(Events.PAUSE,         () => this.onPause())
    w.bind(Events.FINISH,        () => this.onFinish())
    w.bind(Events.PLAY_PROGRESS, (p: unknown) => this.onProgress(p as SCProgressEvent))
    w.bind(Events.ERROR,         (e: unknown) => { this._postMessage(`onPlayerError=${e}`) })
  }

  private swap(): void {
    const data = this.data!
    this.finished = false
    this.trackDuration = 0

    this.widget!.load(`${SOUNDCLOUD_TRACK_API}/${data.id}`, {
      auto_play: true,
      callback: () => {
        this.widget!.setVolume(data.volume)
        if (data.seek > 0) this.widget!.seekTo(data.seek * 1000)
        this.widget!.getDuration(d => { this.trackDuration = Math.round(d / 1000) })
        this._postMessage('playbackReady')
        this.tryResumeAfterVisibility('swap-callback')
      },
    })
    this.logger.info('SC track swapped', JSON.stringify(data))
  }

  private onReady(): void {
    this.logger.info('widget ready')
    const data = this.data!
    this.widget!.setVolume(data.volume)
    if (data.seek > 0) this.widget!.seekTo(data.seek * 1000)
    this.widget!.getDuration(d => { this.trackDuration = Math.round(d / 1000) })
    this._postMessage('playbackReady')
    this.tryResumeAfterVisibility('ready')
  }

  private onPause(): void {
    this.isPlaying = false

    if (this.stoppedByHost || this._externalPause) return

    // Synchronous guard — catches the immediate PAUSE after FINISH
    if (this.finished) return

    this.widget!.getPosition(pos => {
      // Re-check finished inside the async callback — SC may have fired FINISH
      // between the PAUSE event and this callback resolving (async postMessage gap)
      if (this.finished || this._externalPause) return

      const sec = pos / 1000
      if (this.trackDuration > 0 && this.trackDuration - sec > 2) {
        // Also verify widget is actually still paused before resuming —
        // SC can fire spurious PAUSE events mid-playback (e.g. after OPEN_SHARE_PANEL)
        this.widget!.isPaused(paused => {
          if (!paused || this.finished || this._externalPause) return
          this.logger.debug('SC auto-resume')
          this._postMessage('playbackReady')
          this.widget!.play()
        })
      }
    })
  }

  private onFinish(): void {
    this.logger.debug('SC finish')
    this.finished = true
    this.isPlaying = false
    this._postMessage('playbackComplete')
  }

  private onProgress(progress: SCProgressEvent): void {
    this._postMessage(`currentTime=${progress.currentPosition / 1000}`)
  }

  private tryResumeAfterVisibility(reason: string): void {
    if (!this.widget || this.finished || this.stoppedByHost || this._externalPause) return
    if (document.hidden) return

    this.widget.isPaused(paused => {
      if (!paused || this.finished || this.stoppedByHost || this._externalPause) return
      this.logger.debug(`SC resume on ${reason}`)
      this.widget!.play()
    })
  }
}